import {
    Button,
    ButtonWithConfirmationModal,
    defaultIconSize,
    EnumMessageLevel,
    EnumMessageType,
    type IButtonProps,
    type ToolbarElements as StandardToolbarElements,
    type ToolbarOptions as StandardToolbarOption,
} from '@synqo/frontend-component-library';
import { IconPlus, IconTrash } from '@tabler/icons-react';

export type ToolbarOption = 'add' | 'delete' | StandardToolbarOption;

export type ToolbarElements = ToolbarOption[] | ((defaultOptions: ToolbarOption[]) => ToolbarOption[]);

export type ToolbarActionProps = Omit<IButtonProps, 'children' | 'onClick' | 'locked'> & {
    text?: string;
};

export type ActionsProps = {
    addActionProps?: ToolbarActionProps;
    deleteActionProps?: ToolbarActionProps & {
        /**
         * Label for one entry and many (or zero) entries. [one, many/zero]
         */
        contextLabel?: [string, string];
        renderConfirmModalMessage?: (selectedIds: readonly string[]) => { title: string; message: string } | undefined;
    };
};

type UseToolbarElementsOptions = ActionsProps & {
    toolbarElements: ToolbarElements;
    onAddClick: () => void;
    onDeleteClick: () => void;
    addIsLocked: boolean;
    deleteIsLocked: boolean;
    selectedIds: readonly string[];
};

export const useToolbarElements = ({
    toolbarElements,
    onAddClick,
    onDeleteClick,
    addIsLocked,
    deleteIsLocked,
    addActionProps,
    deleteActionProps,
    selectedIds,
}: UseToolbarElementsOptions): StandardToolbarElements => {
    const mapElementToComponent = (element: ToolbarOption): StandardToolbarOption => {
        if (typeof element === 'string') {
            switch (element) {
                case 'add':
                    return (
                        <Button
                            key="btn_add_pos"
                            onClick={onAddClick}
                            appearance={'outlined'}
                            locked={addIsLocked}
                            {...addActionProps}
                        >
                            <IconPlus size={defaultIconSize} />
                            &nbsp;{addActionProps?.text ?? 'Position hinzufügen'}
                        </Button>
                    );
                case 'delete': {
                    const deleteProps = deleteActionProps || {};
                    const contextLabels = deleteProps.contextLabel || ['Position', 'Positionen'];
                    const label = selectedIds.length !== 1 ? contextLabels[1] : contextLabels[0];

                    const defaultModalMessage = {
                        title: `${label} löschen (${selectedIds.length})`,
                        message: `Möchten Sie wirklich ${selectedIds.length} ${label} entfernen?`,
                    };

                    const confirmModalData =
                        deleteProps.renderConfirmModalMessage?.(selectedIds) || defaultModalMessage;

                    return (
                        <ButtonWithConfirmationModal
                            key="btn_del_pos"
                            onClick={onDeleteClick}
                            appearance={'outlined'}
                            locked={deleteIsLocked}
                            {...deleteProps}
                            confirmModalMessage={{
                                ...confirmModalData,
                                level: EnumMessageLevel.Warning,
                                type: EnumMessageType.MultipleChoice,
                            }}
                        >
                            <IconTrash size={defaultIconSize} />
                            &nbsp;{deleteActionProps?.text ?? confirmModalData.title}
                        </ButtonWithConfirmationModal>
                    );
                }
                default:
                    return element;
            }
        }
        return element;
    };

    if (typeof toolbarElements === 'function')
        return (defaultOptions: StandardToolbarOption[]) => toolbarElements(defaultOptions).map(mapElementToComponent);

    return toolbarElements.map(mapElementToComponent);
};
